import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

interface MemberProfile {
  id: string;
  user_id: string | null;
  photo_url: string | null;
  created_at: string;
}

export function GroupMembers() {
  const { user, group, membership } = useAuth();
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  
  const [memberToRemove, setMemberToRemove] = useState<MemberProfile | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [expandedMemberId, setExpandedMemberId] = useState<string | null>(null);

  const isCreator = membership?.role === 'creator';

  // Fetch group members
  const { data: members } = useQuery({
    queryKey: ['group-members', group?.id],
    queryFn: async () => {
      if (!group) return [];
      const { data } = await supabase
        .rpc('get_group_member_profiles', { target_group_id: group.id });
      return (data || []) as MemberProfile[];
    },
    enabled: !!group,
  });

  const handleRemoveMember = async () => {
    if (!memberToRemove || !group) return;

    setIsRemoving(true);

    try {
      const { error } = await supabase
        .rpc('remove_and_block_member', {
          p_group_id: group.id,
          p_member_user_id: memberToRemove.id,
        });

      if (error) {
        console.error('Remove member error:', error);
        toast.error(t('removeMemberFailed'));
      } else {
        toast.success(t('removeMemberSuccess'));
        setExpandedMemberId(null);
        // Refresh member list
        queryClient.invalidateQueries({ queryKey: ['group-members', group.id] });
      }
    } catch {
      toast.error(t('authUnexpectedError'));
    } finally {
      setIsRemoving(false);
      setMemberToRemove(null);
    }
  };

  // Don't show if not creator or no members
  if (!isCreator || !members || members.length <= 1) {
    return null;
  }

  // Filter out the creator from the list
  const otherMembers = members.filter(m => m.id !== user?.id);

  if (otherMembers.length === 0) {
    return null;
  }

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">{t('groupMembersTitle')}</CardTitle>
          <CardDescription>{t('groupMembersDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {otherMembers.map((member) => {
              const isExpanded = expandedMemberId === member.id;
              
              return (
                <Collapsible
                  key={member.id}
                  open={isExpanded}
                  onOpenChange={(open) => setExpandedMemberId(open ? member.id : null)}
                >
                  <CollapsibleTrigger className="flex items-center justify-between w-full p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors text-left">
                    <span className="font-medium">
                      {member.user_id || t('profileTitle')}
                    </span>
                    {isExpanded ? (
                      <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent className="mt-1 px-3 py-3 bg-muted/20 rounded-lg animate-accordion-down">
                    <div className="flex flex-col gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setMemberToRemove(member)}
                        className="justify-start h-auto py-2 px-2 text-sm font-normal"
                      >
                        {t('removeFromChallenge')}
                      </Button>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Remove Member Confirmation Dialog */}
      <AlertDialog open={!!memberToRemove} onOpenChange={(open) => !open && setMemberToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('removeMemberDialogTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('removeMemberDialogDesc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemoving}>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveMember}
              disabled={isRemoving}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isRemoving ? t('removeMemberRemoving') : t('removeMemberConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
